import type { AppState } from '../types';

/**
 * Storage Service - Wrapper pour localStorage et sessionStorage
 */

const STORAGE_VERSION = 2;
const VERSION_KEY = 'app_version';

interface AppStateWrapper {
  version: number;
  timestamp: number;
  data: AppState;
}

/**
 * Internal `get` with function overloads — when a non-null `defaultValue`
 * is provided, the return type narrows to `T` (no more `T | null`). Used
 * by both StorageService and SessionStorage. Runtime behavior unchanged.
 *
 * V2.G12.1: closes the StorageService nullability TS errors that
 * polluted PortfolioDashboard.tsx and other consumers.
 */
function readStorage<T>(storage: Storage, name: string, key: string): T | null;
function readStorage<T>(storage: Storage, name: string, key: string, defaultValue: T): T;
function readStorage<T>(storage: Storage, name: string, key: string, defaultValue: T | null = null): T | null {
  try {
    const item = storage.getItem(key);
    return item ? (JSON.parse(item) as T) : defaultValue;
  } catch (error) {
    console.error(`Error reading from ${name} (${key}):`, error);
    return defaultValue;
  }
}

function localStorageGet<T>(key: string): T | null;
function localStorageGet<T>(key: string, defaultValue: T): T;
function localStorageGet<T>(key: string, defaultValue?: T): T | null {
  return defaultValue === undefined
    ? readStorage<T>(localStorage, 'localStorage', key)
    : readStorage<T>(localStorage, 'localStorage', key, defaultValue);
}

function sessionStorageGet<T>(key: string): T | null;
function sessionStorageGet<T>(key: string, defaultValue: T): T;
function sessionStorageGet<T>(key: string, defaultValue?: T): T | null {
  return defaultValue === undefined
    ? readStorage<T>(sessionStorage, 'sessionStorage', key)
    : readStorage<T>(sessionStorage, 'sessionStorage', key, defaultValue);
}

/**
 * LocalStorage Service
 */
export const StorageService = {
  /**
   * Sauvegarder une valeur
   */
  set<T>(key: string, value: T): boolean {
    try {
      const serialized = JSON.stringify(value);
      localStorage.setItem(key, serialized);
      return true;
    } catch (error) {
      console.error(`Error saving to localStorage (${key}):`, error);
      return false;
    }
  },

  /**
   * Récupérer une valeur — overloaded so that when `defaultValue` is
   * provided, the return type narrows to `T` (instead of `T | null`).
   */
  get: localStorageGet,

  /**
   * Supprimer une valeur
   */
  remove(key: string): boolean {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Error removing from localStorage (${key}):`, error);
      return false;
    }
  },

  /**
   * Vider tout le localStorage
   */
  clear(): boolean {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error('Error clearing localStorage:', error);
      return false;
    }
  },

  /**
   * Vérifier si une clé existe
   */
  has(key: string): boolean {
    return localStorage.getItem(key) !== null;
  },

  /**
   * Obtenir toutes les clés
   */
  keys(): string[] {
    return Object.keys(localStorage);
  },

  /**
   * Sauvegarder l'état complet de l'application
   */
  saveAppState(state: AppState): boolean {
    const appState: AppStateWrapper = {
      version: STORAGE_VERSION,
      timestamp: Date.now(),
      data: state
    };
    return this.set('app_state', appState);
  },

  /**
   * Charger l'état de l'application
   */
  loadAppState(): AppState | null {
    const appState = this.get<AppStateWrapper>('app_state');

    if (!appState) return null;

    // Vérifier la version
    if (appState.version < STORAGE_VERSION) {
      console.warn(`App version outdated: ${appState.version} < ${STORAGE_VERSION}. Running migration...`);
      // TODO: Implémenter migration si nécessaire
      return appState.data;
    }

    return appState.data;
  },

  /**
   * Export des données en JSON
   */
  exportData(): Record<string, any> {
    const data: Record<string, any> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        data[key] = this.get(key);
      }
    }
    return data;
  },

  /**
   * Import des données depuis JSON
   */
  importData(data: Record<string, any>): boolean {
    try {
      Object.entries(data).forEach(([key, value]) => {
        this.set(key, value);
      });
      return true;
    } catch (error) {
      console.error('Error importing data:', error);
      return false;
    }
  },

  /**
   * Taille utilisée en localStorage (approximative)
   */
  getSize(): number {
    let size = 0;
    // Use Object.prototype.hasOwnProperty.call instead of accessing the
    // method directly on `localStorage` — the prototype lookup is the
    // safe pattern (no-prototype-builtins). It also guards against the
    // pathological case where a key shadows the built-in.
    for (const key in localStorage) {
      if (Object.prototype.hasOwnProperty.call(localStorage, key)) {
        size += localStorage[key].length + key.length;
      }
    }
    return size;
  },

  /**
   * Taille en format lisible
   */
  getSizeFormatted(): string {
    const bytes = this.getSize();
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  }
};

/**
 * Session Storage Service (données temporaires pour la session)
 */
export const SessionStorage = {
  set<T>(key: string, value: T): boolean {
    try {
      const serialized = JSON.stringify(value);
      sessionStorage.setItem(key, serialized);
      return true;
    } catch (error) {
      console.error(`Error saving to sessionStorage (${key}):`, error);
      return false;
    }
  },

  get: sessionStorageGet,

  remove(key: string): boolean {
    try {
      sessionStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Error removing from sessionStorage (${key}):`, error);
      return false;
    }
  },

  clear(): boolean {
    try {
      sessionStorage.clear();
      return true;
    } catch (error) {
      console.error('Error clearing sessionStorage:', error);
      return false;
    }
  }
};
